import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { AlertService, FormValidationService, FormValidator, LocalParserFormatter } from 'core-lib';
import { ErrorHandlingService, GuidService, GooglePlacesService } from 'core-lib';
import { ApplicationDetailsViewModel, ApplicationVehicleViewModel } from './application.vm';
import {
    ApplicationService, ApplicationDetails,
    CreateApplicationResponseData, ApplicationVehicle, ApplicationLOVsResponseData, ValidateRegoReponseData
} from '../services/application.service';

import * as _ from 'lodash';
import * as moment from 'moment';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FileUploadService } from '../services/file-upload.service';
import { FileListItemModel } from '../components/file-upload/file-upload.component';
import { TermsAndConditionsModalComponent } from '../modals/terms-and-conditions-modal/terms-and-conditions-modal.component';
import { BackflowTooltipModalComponent } from '../modals/backflow-tooltip-modal/backflow-tooltip-modal.component';
import { EmsTooltipModalComponent } from '../modals/ems-tooltip-modal/ems-tooltip-modal.component';
import { VehicleExistsWarningModalComponent } from '../modals/vehicle-exists-warning-modal/vehicle-exists-warning-modal.component';
import { environment } from '../../environments/environment';

@Component({
    selector: 'cli-application',
    templateUrl: './application.component.html',
    styleUrls: ['./application.component.scss']
})
export class ApplicationComponent implements OnInit {

    @ViewChild('tcModal')
    public tcModal: TermsAndConditionsModalComponent;

    @ViewChild('backflowTooltipModal')
    public backflowTooltipModal: BackflowTooltipModalComponent;

    @ViewChild('emsTooltipModal')
    public emsTooltipModal: EmsTooltipModalComponent;

    @ViewChild('vehicleExistsWarningModal')
    public vehicleExistsWarningModal: VehicleExistsWarningModalComponent;

    public vm: ApplicationDetailsViewModel;
    public googlePlacesOptions: any;
    public validator: FormValidator;
    public regoChanged$: Subject<ApplicationVehicleViewModel>;
    public stateChanged$: Subject<string>;

    constructor(
        private appSvc: ApplicationService,
        private formValidationSvc: FormValidationService,
        private formatter: LocalParserFormatter,
        private errorHandlingSvc: ErrorHandlingService,
        private guidSvc: GuidService,
        private router: Router,
        private fileUploadSvc: FileUploadService,
        private alertSvc: AlertService,
        private googlePlacesSvc: GooglePlacesService) {

        this.vm = new ApplicationDetailsViewModel();
        this.validator = this.formValidationSvc.getFormValidator(this.vm);

        this.googlePlacesOptions = googlePlacesSvc.defaultOptions;

        this.addNewVehicle();

        this.vm.startDate = this.formatter.fromMoment(moment());
        this.vm.isOpening = true;

        this.regoChanged$ = new Subject<ApplicationVehicleViewModel>();
        this.stateChanged$ = new Subject<string>();
    }


    ngOnInit() {

        this.appSvc.getApplicationLOVs().toPromise()
            .then((data: ApplicationLOVsResponseData) => {
                this.vm.permitFee = data.permitRate;
                this.vm.usageFee = data.usageRate;

                this.vm.businessTypes = _.map(data.businessTypes, (n: any) => {
                    return {
                        businessTypeId: n.businessTypeId,
                        description: n.description
                    };
                });

                this.vm.vehicleTypes = _.map(data.vehicleTypes, (n: any) => {
                    return {
                        vehicleTypeId: n.vehicleTypeId,
                        description: n.description
                    };
                });

                this.vm.vehicleMakes = _.map(data.vehicleMakes, (n: any) => {
                    return {
                        vehicleMakeId: n.vehicleMakeId,
                        description: n.description
                    };
                });
                this.calculateTotalCost();

                this.vm.isOpening = false;
            }, (e) => {


                this.vm.isOpening = false;

                let errorMessage = this.errorHandlingSvc.processHttpError(e);
                return Promise.reject(errorMessage);

            });

        this.stateChanged$.pipe(
            debounceTime(500),
            distinctUntilChanged()
        ).subscribe((v: string) => {
            this.vm.state = v.toUpperCase();
        });

        this.regoChanged$.pipe(
            debounceTime(800),
            distinctUntilChanged((x: any, y: any) => {
                return x.regoNo !== y.regoNo || x.state !== y.state;
            })
        ).subscribe((v: ApplicationVehicleViewModel) => {

            v.errors = [];

            v.regoNo = v.regoNo.toUpperCase();
            v.state = v.state.toUpperCase();

            let validator = this.formValidationSvc.getFormValidator(v);
            validator.validateState('state', 'State');

            if (v.regoNo === '' || v.state === '') {
                return;
            }

            if (v.regoNo.trim().indexOf(' ') > 0) {
                v.regoNo = v.regoNo.replace(' ', '');
            }

            this.validateRego(validator, v);

        });
    }

    validateRego(validator: FormValidator, v: ApplicationVehicleViewModel): Promise<boolean> {

        return this.appSvc.validateRego(v.regoNo, v.state).toPromise<ValidateRegoReponseData>().then(
            (data: ValidateRegoReponseData) => {
                validator.validateCustom('regoNo', 'An active record of a vehicle with this registration already exists in the system. ' +
                    'Please contact support for assistance', () => !data.isValid);

                if (!data.isValid) {

                    this.vehicleExistsWarningModal.rego = v.regoNo;
                    this.vehicleExistsWarningModal.state = v.state;
                    return this.vehicleExistsWarningModal.openModal().toPromise().then(() => {
                        return Promise.reject('Invalid Input');
                    });

                }

                return Promise.resolve(true);
            },
            (e) => {
                let err = this.errorHandlingSvc.processHttpError(e);
                return Promise.reject(err);
            }
        );
    }

    public onStateChanged(event: any): void {
        this.stateChanged$.next(event.target.value);
    }

    public onRegoChanged(event: any, v: ApplicationVehicleViewModel): void {
        this.regoChanged$.next(v);
    }

    public addNewVehicle() {

        let v = new ApplicationVehicleViewModel();
        v.vehicleId = this.guidSvc.getGuid(),
            v.regoNo = '';
        v.state = '';

        this.vm.vehicles.push(v);
        this.calculateTotalCost();
    }

    public removeVehicle() {
        this.vm.vehicles.splice(this.vm.vehicles.length - 1, 1);
        this.calculateTotalCost();
    }


    calculateTotalCost() {
        this.vm.totalCost = this.vm.vehicles.length * this.vm.permitFee;
    }

    public error(vm: any, fieldName: string): string {
        return this.formValidationSvc.getErrorMessage(vm, fieldName);
    }

    validate(): Promise<void> {
        this.vm.errors = [];

        let regoValidations = [];

        _.forEach(this.vm.vehicles, (v: ApplicationVehicleViewModel) => {
            v.errors = [];

            let vehicleValidator = this.formValidationSvc.getFormValidator(v);

            let isRego = vehicleValidator.validateRequired('regoNo', 'Registration No');
            isRego = isRego && vehicleValidator.validateMaxLength('regoNo', 'Registration No', 8);

            isRego = isRego && vehicleValidator.validateCustom('regoNo',
                'Registration No must contain only alphanumerical characters (like XEL730).',
                () => {
                    let reAlphaNumerical = /^[a-zA-Z0-9]*$/;
                    let isAlphaNumerical = reAlphaNumerical.test(v.regoNo);
                    return !isAlphaNumerical;
                });

            let isState = vehicleValidator.validateRequired('state', 'State');
            isState = isState && vehicleValidator.validateState('state', 'State');

            let isCapacity = vehicleValidator.validateRequired('capacity', 'Capacity');
            isCapacity = isCapacity && vehicleValidator.validateNumber('capacity', 'Capacity');
            isCapacity = isCapacity && vehicleValidator.validateMinValue('capacity', 'Capacity', 100);
            isCapacity = isCapacity && vehicleValidator.validateMaxValue('capacity', 'Capacity', 99999);

            vehicleValidator.validateRequired('vehicleMakeId', 'Vehicle Make');
            vehicleValidator.validateRequired('vehicleTypeId', 'Vehicle Type');

            vehicleValidator.validateCustom('isBackflowPreventionDeviceSetup',
                'You need to specify if your vehicle is fitted with a backflow prevention device.',
                () => {
                    return v.isBackflowPreventionDeviceSetup === undefined;
                });



            if (v.isBackflowPreventionDeviceSetup) {

                vehicleValidator.validateCustom('backflowDeviceType',
                    'You need to specify the type of backflow prevention device.',
                    () => {
                        return !v.isAirGap && !v.isRPZD;
                    });

                vehicleValidator.validateCustom('isBackflowPreventionDeviceCertified',
                    'You need to specify if your backflow prevention device was tested and certified.',
                    () => {
                        return v.isBackflowPreventionDeviceCertified === undefined;
                    });
            } 

            if (v.isBackflowPreventionDeviceSetup === false) {

                vehicleValidator.validateCustom('isBackflowPreventionDeviceSetup',
                    'Your vehicle needs to be fitted with a backflow prevention device. ', () => { return true });

            }

            if(v.isBackflowPreventionDeviceCertified === false)
            {
                vehicleValidator.validateCustom('isBackflowPreventionDeviceCertified',
                    'Your vehicle backflow prevention device needs to be tested and certified. ', () => { return true });
            }

            if (v.isBackflowPreventionDeviceSetup && v.isBackflowPreventionDeviceCertified) {
                let isExpiryDate = vehicleValidator.validateRequired('backflowPreventionCertificateIssueDate',
                    'Certificate Issue Date');

                if (isExpiryDate) {

                    let vd = this.formatter.toMoment(v.backflowPreventionCertificateIssueDate);
                    let t = moment().startOf('day').toDate();
                    let t12 = moment().add(-12, 'months').startOf('day').toDate();

                    let isExpiryDateValid = vehicleValidator.validateCustom('backflowPreventionCertificateIssueDate',
                        'Certificate Issue Date must be a valid date',
                        () => { return vd == null || !vd.isValid(); });


                    isExpiryDateValid = isExpiryDateValid && vehicleValidator.validateCustom('backflowPreventionCertificateIssueDate',
                        'Certificate Issue Date cannot be in the future',
                        () => { return vd.toDate() > t; });


                    isExpiryDateValid = isExpiryDateValid && vehicleValidator.validateCustom('backflowPreventionCertificateIssueDate',
                        'Certificate Issue Date cannot be older than 12 months',
                        () => { return vd.toDate() < t12; });
                }

                vehicleValidator.validateCustom('uploadCertificate',
                    'You need to upload a copy of the certificate',
                    () => {
                        return v.fileList.items.length === 0;
                    });
            }

            if (isRego && isState) {
                regoValidations.push(this.validateRego(vehicleValidator, v));
            }
            if (v.errors.length > 0) {
                regoValidations.push(Promise.reject('Invalid Input'));
            }
        });

        this.validator.validateRequired('clientName', 'Business Name');
        this.validator.validateMaxLength('clientName', 'Business Name', 50);
        this.validator.validateRequired('firstName', 'First Name');
        this.validator.validateMaxLength('firstName', 'First Name', 50);
        this.validator.validateRequired('lastName', 'Last Name');
        this.validator.validateMaxLength('lastName', 'Last Name', 50);
        this.validator.validateRequired('email', 'Email');
        this.validator.validateEmail('email', 'Email');
        this.validator.validateMaxLength('email', 'Email', 50);
        this.validator.validateCustom('abn', 'ABN must contain 11 digits or be empty',
            () => { return this.vm.abn && !this.vm.abn.match(/^(\d *?){11}$/) });

        this.validator.validateRequired('workPhone', 'Work Phone');
        this.validator.validatePhone('workPhone', 'Work Phone');
        this.validator.validateRequired('mobilePhone', 'Mobile Phone');
        this.validator.validateMobile('mobilePhone', 'Mobile Phone');

        this.validator.validateRequired('address1', 'Address Line 1');
        this.validator.validateMaxLength('address1', 'Address Line 1', 100);
        this.validator.validateMaxLength('address2', 'Address Line 2', 100);
        this.validator.validateRequired('suburb', 'Suburb');
        this.validator.validateMaxLength('suburb', 'Suburb', 50);
        this.validator.validateRequired('state', 'State');
        this.validator.validateState('state', 'State');
        this.validator.validateRequired('postCode', 'Postcode');
        this.validator.validateMinLength('postCode', 'Postcode', 4);
        this.validator.validateMaxLength('postCode', 'Postcode', 4);
        this.validator.validateCustom('postCode', 'Postcode must contain only numeric characters',
            () => { return this.vm.postCode && !this.vm.postCode.match(/^\d+$/) });

        if (!this.vm.isSameAddress) {
            this.validator.validateRequired('billingAddress1', 'Address Line 1');
            this.validator.validateMaxLength('billingAddress1', 'Address Line 1', 100);
            this.validator.validateMaxLength('billingAddress2', 'Address Line 2', 100);
            this.validator.validateRequired('billingSuburb', 'Suburb');
            this.validator.validateMaxLength('billingSuburb', 'Suburb', 50);
            this.validator.validateRequired('billingState', 'State');
            this.validator.validateState('billingState', 'State');
            this.validator.validateRequired('billingPostCode', 'Postcode');
            this.validator.validateMinLength('billingPostCode', 'Postcode', 4);
            this.validator.validateMaxLength('billingPostCode', 'Postcode', 4);
            this.validator.validateCustom('billingPostCode', 'Postcode must contain only numeric characters',
                () => { return this.vm.billingPostCode && !this.vm.billingPostCode.match(/^\d+$/) });
        }


        this.validator.validateRequired('businessTypeId', 'Type of Business');
        if (this.vm.businessTypeId && this.vm.businessTypeId.toUpperCase() === '59BA4193-9F7B-48DE-B2C1-B7BD7D2D5AD1') {
            this.validator.validateRequired('businessTypeOther', 'Business Description');
        }


        this.validator.validateRequired('startDate', 'Start Date');

        let dt = this.formatter.toMoment(this.vm.startDate);
        let today = moment().startOf('day').toDate();

        let isDate = this.validator.validateCustom('startDate', 'Start Date must be a valid date',
            () => { return dt == null || !dt.isValid(); });

        if (isDate) {
            let isBeforeToday = this.validator.validateCustom('startDate', 'Start Date must be must be today or in the future',
                () => { return dt.toDate() < today; });
        }


        this.validator.validateCustom('isEmsKey', 'You need to specify if you require EMS Key',
            () => {
                return this.vm.isEmsKey === undefined;
            });


        this.validator.validateCustom('isTCRead', 'You need to read and accept Terms and Conditions',
            () => {
                return !this.vm.isTCRead;
            });


        return Promise.all(regoValidations).then((results: any[]) => {

            if (this.vm.errors.length > 0) {
                return Promise.reject('Invalid Input');
            }

            return Promise.resolve();
        },
            (e) => { return Promise.reject('Invalid Input'); });

    }

    public clearDevices(v: ApplicationVehicleViewModel) {
        v.isAirGap = false;
        v.isRPZD = false;
    }

    public clearCerficate(v: ApplicationVehicleViewModel) {
        v.backflowPreventionCertificateIssueDate = null;

        if (v.fileList.items.length > 0) {
            _.forEach(v.fileList.items, (f: FileListItemModel) => {
                this.fileUploadSvc.deleteFile(f.fileId).toPromise().then(() => { },
                    (e) => { this.errorHandlingSvc.processHttpError(e); });
            });

            v.fileList.items = [];
        }
    }

    public submit(): Promise<void> {

        return this.validate().then(() => {

            let data = new ApplicationDetails();
            data.clientName = this.vm.clientName;
            data.firstName = this.vm.firstName;
            data.lastName = this.vm.lastName;
            data.abn = this.vm.abn;

            data.email = this.vm.email;
            data.workPhone = this.vm.workPhone;
            data.mobilePhone = this.vm.mobilePhone;

            data.address1 = this.vm.address1;
            data.address2 = this.vm.address2;
            data.suburb = this.vm.suburb;
            data.postCode = this.vm.postCode;
            data.state = this.vm.state;
            data.isAddressNotFound = this.vm.isAddressNotFound;

            data.billingAddress1 = this.vm.billingAddress1;
            data.billingAddress2 = this.vm.billingAddress2;
            data.billingSuburb = this.vm.billingSuburb;
            data.billingPostCode = this.vm.billingPostCode;
            data.billingState = this.vm.billingState;
            data.isBillingAddressNotFound = this.vm.isBillingAddressNotFound;

            data.isSameAddress = this.vm.isSameAddress;

            data.businessTypeId = this.vm.businessTypeId;
            data.businessTypeOther = this.vm.businessTypeOther;

            data.vehicles = _.map(this.vm.vehicles, (vm: ApplicationVehicleViewModel) => {

                let v = new ApplicationVehicle();

                v.vehicleId = vm.vehicleId;
                v.regoNo = vm.regoNo;
                v.regoState = vm.state;
                v.capacity = vm.capacity;
                v.vehicleTypeId = vm.vehicleTypeId;
                v.vehicleMakeId = vm.vehicleMakeId;
                v.isAirGap = vm.isAirGap;
                v.isRPZD = vm.isRPZD;
                v.backflowPreventionCertificateIssueDate = this.formatter.toIsoString(vm.backflowPreventionCertificateIssueDate);

                return v;
            });

            data.startDate = this.formatter.toIsoString(this.vm.startDate);
            data.isEmsKey = this.vm.isEmsKey;
            data.isAdminCreated = false;

            this.vm.isLoading = true;

            this.appSvc.submitApplication(data).toPromise().then((r: CreateApplicationResponseData) => {

                if (r.isDuplicatePermitHolder) {
                    let errorMessage = 'Duplicate customer. Permit details already exist in the system. Please contact the Hydrotrak Team for assistance.';
                    this.alertSvc.error(environment.TOASTR_HEADER, errorMessage, 6000);
                    this.vm.isLoading = false;
                    return Promise.reject(errorMessage);
                }


                this.vm.isLoading = false;
                this.router.navigateByUrl('application-video');

              

            },
                (e) => {
                    let errorMessage = this.errorHandlingSvc.processHttpError(e);
                    this.vm.isLoading = false;
                    return Promise.reject(errorMessage);
                });
        },
            () => { });
    }

    public addressNotFoundChanged() {
        if (this.vm.isAddressNotFound) {
            this.vm.addressSearch = '';
        }
    }

    public billingAddressNotFoundChanged() {
        if (this.vm.isBillingAddressNotFound) {
            this.vm.billingAddressSearch = '';
        }
    }

    public handleAddressChange(googleAddr: any) {

        let addr = this.googlePlacesSvc.toAddress(googleAddr);

        this.vm.address1 = addr.address1;
        this.vm.address2 = addr.address2;
        this.vm.suburb = addr.suburb;
        this.vm.state = addr.state;
        this.vm.postCode = addr.postCode;

        this.vm.addressSearch = '';

        this.updateBillingAddress();
    }

    public handleBillingAddressChange(googleAddr: any) {

        let addr = this.googlePlacesSvc.toAddress(googleAddr);

        this.vm.billingAddress1 = addr.address1;
        this.vm.billingAddress2 = addr.address2;
        this.vm.billingSuburb = addr.suburb;
        this.vm.billingState = addr.state;
        this.vm.billingPostCode = addr.postCode;

        this.vm.billingAddressSearch = '';
    }


    public clearBillingAddress() {
        this.vm.billingAddress1 = '';
        this.vm.billingAddress2 = '';
        this.vm.billingPostCode = '';
        this.vm.billingState = '';
        this.vm.billingSuburb = '';
    }

    public copyBusinessAddress() {
        this.vm.billingAddress1 = this.vm.address1;
        this.vm.billingAddress2 = this.vm.address2;
        this.vm.billingPostCode = this.vm.postCode;
        this.vm.billingState = this.vm.state;
        this.vm.billingSuburb = this.vm.suburb;
        this.vm.isBillingAddressNotFound = this.vm.isBillingAddressNotFound;
    }

    public updateBillingAddress() {
        if (!this.vm.isSameAddress) {
            return;
        }

        this.copyBusinessAddress();
    }

    public showTc() {
        this.tcModal.header = 'Terms and Conditions';
        this.tcModal.openModal();
    }

    public formatPhone(fieldName: string) {
        let value = this.vm[fieldName];
        if (value && value !== '' && !this.validator.failsAUPhone(value)) {
            this.vm[fieldName] = this.validator.formatPhone(value);
        }
    }

    public showBackflowToolTip() {
        this.backflowTooltipModal.openModal();
    }

    public showEmsToolTip() {
        this.emsTooltipModal.openModal();
    }

    public hasErrors(): boolean {
        return this.vm.errors.length > 0 || !!_.find(this.vm.vehicles, (v) => { return v.errors.length > 0; });
    }
}
